/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import {
  AnvilorFormsApp,
  AnvilorFormsAppStore,
  AnvilorFormsAppStoreContext,
} from "../anvilor-forms-app-store.service";
import { EMPTY, iif, Observable, of, pipe, switchMap, tap, withLatestFrom } from "rxjs";
import { AnvilorNotificationsService, ApiCallStatus } from "@anvilor/ngx-apps-core";
import { RuiInstance } from "@anvilor/ngx-rui";
import { map, take } from "rxjs/operators";
import { AnvilorFormsApiService } from "../../services/anvilor-forms-api.service";
import {
  AnvilorFormResponseDocument,
  OnSubmitEelResult,
  ResponseSubmissionUxActions,
} from "../../models/anvilor-form-response-document";
import { HttpErrorResponse } from "@angular/common/http";
import { PostFormResponseEventData } from "../../models/post-form-response-event-data";

// A null result means the form has no on_submit logic (e.g. a default
// submission_handling_config) - the submission was accepted as-is.
const submissionWasAccepted = (result: OnSubmitEelResult | null): boolean =>
  (result?.result_code ?? "success") === "success";

export class AnvilorFormsAppSubmit {
  private app: AnvilorFormsApp;
  private rui: RuiInstance;
  private store: AnvilorFormsAppStore;
  private formsApi: AnvilorFormsApiService;
  private notify: AnvilorNotificationsService;

  postSubmissionStatus$: Observable<ApiCallStatus>;
  isPosting$: Observable<boolean>;
  submissionAccepted$: Observable<boolean>;
  forceEnableSubmit$: Observable<boolean>;

  constructor(app: AnvilorFormsApp, ctx: AnvilorFormsAppStoreContext) {
    this.app = app;
    this.rui = ctx.ruiInstance;
    this.store = ctx.store;
    this.formsApi = ctx.formsApi;
    this.notify = ctx.notify;

    this.postSubmissionStatus$ = this.store.select(
      (state) => state.post_response_call_status
    );

    this.submissionAccepted$ = this.store.select((state) => state.submission_accepted);

    this.isPosting$ = this.postStatusIs("active");
    this.forceEnableSubmit$ = this.store.select((state) => state.force_enable_submit);
  }

  doSubmit(options?: {
    payload?: any;
    onSuccess?: (doc: AnvilorFormResponseDocument) => void;
    onError?: () => void;
  }) {
    const withLatestPostData = () =>
      pipe(
        withLatestFrom(this.rui.currentData$, this.store.state$),
        map(
          ([_, ruiData, state]): PostFormResponseEventData => ({
            response_data: ruiData,
            anvilor_form_oid: state.anvilor_form_config!.anvilor_form_oid,
            attachments: Object.values(this.rui.fileUploads.getUploads()),
            edit_key: state.edit_config?.edit_key,
            deployment_key: state.anvilor_form_config!.deployment_key,
            deployment_token: state.anvilor_form_config!.deployment_token,
            response_oid: state.edit_config?.response_oid,
            auth_header: state.submission_post_auth_header,
            grecaptcha_v2_response: state.grecaptcha_v2_response,
            payload: options?.payload,
          })
        )
      );

    of(null)
      .pipe(
        take(1),
        tap(() => this.setPostStatus("active")),
        withLatestPostData(),
        switchMap((data) => this.formsApi.postSubmission(data)),
        withLatestFrom(this.store.displayConfig$),
        tap({
          next: ([responseDoc, _]) => {
            this.onSuccessPost(responseDoc);
            options?.onSuccess ? options.onSuccess(responseDoc) : null;
          },
          error: (err: HttpErrorResponse) => {
            this.onErrorPost(err);
            options?.onError ? options.onError() : null;
          },
        }),
        // meh - if client data enabled we refresh after a submission since it is
        // likely to change? Need to think through this a bit more.
        switchMap(([_, displayConfig]) =>
          iif(
            () => displayConfig.enable_client_data ?? false,
            this.app.loading.clientDataLoader.loadClientData(),
            EMPTY
          )
        )
      )
      .subscribe();
  }

  postStatusIs(...statuses: ApiCallStatus[]): Observable<boolean> {
    return this.postSubmissionStatus$.pipe(map((v) => statuses.includes(v)));
  }

  private setPostStatus(post_response_call_status: ApiCallStatus): void {
    this.store.patchState({ post_response_call_status });
  }

  private onSuccessPost(responseDoc: AnvilorFormResponseDocument): void {
    this.rui.executePatches("onSuccessPost", [
      { op: "set", path: "", value: responseDoc.response.response_data },
    ]);
    this.store.patchState({ response_client_data: responseDoc.response.client_data });
    this.setPostStatus("success");
    this.handleOnSubmitResult(responseDoc);
  }

  private handleOnSubmitResult(responseDoc: AnvilorFormResponseDocument): void {
    const submissionEvents = responseDoc.response.submission_events ?? [];
    if (!submissionEvents || submissionEvents.length === 0) {
      return; // Possible?
    }
    const lastEvent = submissionEvents[submissionEvents.length - 1];
    const result = lastEvent.on_submit_eel_result;
    this.store.patchState({ submission_accepted: submissionWasAccepted(result) });
    this.doSubmissionUxActions(result.submission_ux_actions ?? {});
  }

  private doSubmissionUxActions(actions: ResponseSubmissionUxActions): void {
    if (!!actions.redirect_url) {
      window.location.assign(actions.redirect_url);
    }
    const toasts = actions.toasts ?? [];
    toasts.forEach((toastCommand) => this.notify.toast.toastFromCommand(toastCommand));
  }

  private onErrorPost(err: HttpErrorResponse) {
    this.setPostStatus("error");
    this.notify.error.passive({
      title: "Error",
      error_code: "RESPONSE_FAILURE",
      body: err.error?.message ?? "Failed to save response.",
      server_error_info: err.error,
    });
    this.setPostStatus("none");
  }
}
